import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import SEO from "@/components/SEO";
import { useSettings } from "@/lib/settings";
import { trackGenerateLead } from "@/lib/analytics";
import { Phone } from "lucide-react";

const GALLERY = [
  "https://images.pexels.com/photos/36430149/pexels-photo-36430149.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "https://images.unsplash.com/photo-1577219491135-ce391730fb2c?crop=entropy&cs=srgb&fm=jpg&q=85&w=940",
  "https://images.pexels.com/photos/5014596/pexels-photo-5014596.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "https://images.pexels.com/photos/14062147/pexels-photo-14062147.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "https://images.pexels.com/photos/18606626/pexels-photo-18606626.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "https://images.pexels.com/photos/33939170/pexels-photo-33939170.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
];

export default function EventChefPage() {
  const { settings } = useSettings();
  const [form, setForm] = useState({ name: "", email: "", phone: "", event_date: "", guests: 10, location: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/events/book", { ...form, guests: parseInt(form.guests || "1") });
      trackGenerateLead("event_chef");
      toast.success("Booking request sent! We'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", event_date: "", guests: 10, location: "", notes: "" });
    } catch (err) {
      toast.error("Could not submit. Try again.");
    } finally { setSubmitting(false); }
  };

  return (
    <div data-testid="event-chef-page">
      <SEO
        title="Private Event Chef · Weddings, Parties, Corporate"
        description="Hire a seafood chef for weddings, private dinners, and corporate events. Bespoke menus, on-site cooking, premium experience."
        path="/event-chef"
      />
      <section className="bg-[color:var(--color-primary)] text-white py-20">
        <div className="container-x px-6">
          <div className="text-xs uppercase tracking-[0.3em] text-[color:var(--color-accent)] mb-4">Premium Hospitality</div>
          <h1 className="font-display text-5xl md:text-6xl">Event Chef</h1>
          <p className="text-white/70 max-w-2xl mt-4">Hire a fish chef for barbecues, parties, and events. Fresh fish, expertly cooked, served at your venue.</p>
          <a href={`tel:${settings.phone}`} className="btn-accent mt-8 inline-flex" data-testid="event-call-btn">
            <Phone className="w-4 h-4" /> Call to Book
          </a>
        </div>
      </section>

      <section className="section bg-[color:var(--color-bg)]">
        <div className="container-x">
          <h2 className="font-display text-3xl text-[color:var(--color-primary)] mb-10">Past Events</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {GALLERY.map((src, i) => (
              <div key={i} className="aspect-square overflow-hidden rounded-lg" data-testid={`gallery-img-${i}`}>
                <img src={src} alt={`Event ${i+1}`} className="w-full h-full object-cover hover:scale-105 transition duration-500" />
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section bg-white">
        <div className="container-x max-w-2xl">
          <h2 className="font-display text-4xl text-[color:var(--color-primary)] mb-2">Request a Booking</h2>
          <p className="text-[color:var(--color-text-secondary)] mb-8">Tell us about your event and we'll get back within 24 hours.</p>
          <form onSubmit={submit} className="space-y-4">
            {[
              { name: "name", label: "Your Name", type: "text" },
              { name: "email", label: "Email", type: "email" },
              { name: "phone", label: "Phone", type: "tel" },
              { name: "event_date", label: "Event Date", type: "date" },
              { name: "guests", label: "Number of Guests", type: "number" },
              { name: "location", label: "Event Location", type: "text" },
            ].map((f) => (
              <div key={f.name}>
                <label className="text-xs uppercase tracking-widest text-gray-600">{f.label}</label>
                <input
                  required type={f.type} min={f.type === "number" ? 1 : undefined}
                  value={form[f.name]}
                  onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                  className="w-full mt-1 border border-[color:var(--color-border)] rounded px-3 py-2"
                  data-testid={`event-input-${f.name}`}
                />
              </div>
            ))}
            <div>
              <label className="text-xs uppercase tracking-widest text-gray-600">Special Requests</label>
              <textarea
                rows="4"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="w-full mt-1 border border-[color:var(--color-border)] rounded px-3 py-2"
                data-testid="event-input-notes"
              />
            </div>
            <button type="submit" disabled={submitting} className="btn-accent" data-testid="event-submit-btn">
              {submitting ? "Sending…" : "Submit Booking Request"}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
